import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { isDatePassed, toDate } from 'shared/utils/date';

export const currentYear = new Date().getFullYear();
export const minPeriodYear = currentYear - 10;
export const maxPeriodYear = currentYear + 10;

export const StyledTooltipWrapper = styled(TrueImpactText)`
  padding: 16px;
  .bold {
    font-weight: bold;
  }
`;

export type CardPeriodType =
  | 'calendarYear'
  | 'academicYear'
  | 'fiscalYear'
  | 'customYear';

export type YearSelectionArray = [
  YearKeyValuePair | undefined,
  YearKeyValuePair,
  YearKeyValuePair,
  YearKeyValuePair,
  YearKeyValuePair | undefined,
];

export interface YearKeyValuePair {
  text: string;
  value: number;
}

export interface ParentPeriodValue {
  option: string;
  startDate: string | null | undefined; // API sends `null`
  endDate: string | null | undefined;
}

export interface PeriodValue {
  option: CardPeriodType;
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export interface PeriodString {
  option: CardPeriodType;
  startDate: string;
  endDate: string;
}

export type OnChangePeriod = (value: PeriodValue) => unknown;

export interface PeriodsProps {
  isThirdParty: boolean;
  periodType?: CardPeriodType;
  startDate?: Date;
  endDate?: Date;
  showMoreYears: boolean;
  reportId: number;
  reportYear?: string;
  setSelectedCard: (
    periodType?: CardPeriodType,
    start?: Date,
    end?: Date,
  ) => unknown;
  onChangePeriod: OnChangePeriod;
}

export const getInitialYearValue = (
  startDate: PeriodsProps['startDate'],
  endDate: PeriodsProps['endDate'],
  yearFormatter: (year: number) => string,
): YearKeyValuePair | undefined => {
  if (!startDate || !endDate) return undefined;
  const year = new Date(startDate).getFullYear();
  return {
    value: year,
    text: yearFormatter(year),
  };
};

export const getYearsPeriod = (
  formatter: (year: number) => string,
  year: number | undefined = currentYear,
): YearSelectionArray => {
  const years: YearSelectionArray = [
    {
      value: year - 2,
      text: formatter(year - 2),
    },
    {
      value: year - 1,
      text: formatter(year - 1),
    },
    {
      value: year,
      text: formatter(year),
    },
    {
      value: year + 1,
      text: formatter(year + 1),
    },
    {
      value: year + 2,
      text: formatter(year + 2),
    },
  ];
  return years;
};

export const onPreviousYearPeriod = (
  years: YearSelectionArray,
  formatter: (year: number) => string,
): YearSelectionArray => {
  const newYears: YearSelectionArray = [...years];
  if (!newYears[0]) return years;
  const previousYear = newYears[0].value - 1;
  const previous =
    previousYear < minPeriodYear
      ? undefined
      : {
          value: previousYear,
          text: formatter(previousYear),
        };
  for (let i = 4; i > 0; i--) {
    newYears[i] = newYears[i - 1];
  }
  newYears[0] = previous;
  return newYears;
};

export const onNextYearPeriod = (
  years: YearSelectionArray,
  formatter: (year: number) => string,
): YearSelectionArray => {
  const newYears: YearSelectionArray = [...years];
  if (!newYears[4]) return years;
  const nextYear = newYears[4].value + 1;
  const next =
    nextYear > maxPeriodYear
      ? undefined
      : {
          value: nextYear,
          text: formatter(nextYear),
        };
  for (let i = 0; i < 4; i++) {
    newYears[i] = newYears[i + 1];
  }
  newYears[4] = next;
  return newYears;
};

const matchWith = (
  option: string,
  match: string,
  defaultText = '',
): string =>
  option.toLowerCase().includes(match.toLowerCase())
    ? option
    : defaultText || '';

const defaultPeriod = {
  startDate: undefined,
  endDate: undefined,
  card: undefined,
};

const parsePeriodValuesObject = (
  value: ParentPeriodValue | null,
  options: string[],
): {
  startDate: Date | undefined;
  endDate: Date | undefined;
  card: CardPeriodType | undefined;
} => {
  if (!value || !value.option) return defaultPeriod;
  switch (value.option) {
    case matchWith(value.option, 'calendarYear'):
      return {
        card: 'calendarYear',
        startDate: toDate(value.startDate),
        endDate: toDate(value.endDate),
      };
    case matchWith(value.option, 'academicYear'):
      return {
        card: 'academicYear',
        startDate: toDate(value.startDate),
        endDate: toDate(value.endDate),
      };
    case matchWith(value.option, 'fiscalYear'):
      return {
        card: 'fiscalYear',
        startDate: toDate(value.startDate),
        endDate: toDate(value.endDate),
      };
    case matchWith(value.option, 'customYear'):
      return {
        card: 'customYear',
        startDate: toDate(value.startDate),
        endDate: toDate(value.endDate),
      };
    case matchWith(value.option, 'Other'):
      return {
        card: 'customYear',
        startDate: toDate(value.startDate),
        endDate: toDate(value.endDate),
      };
    default:
      if (
        value.option.startsWith('This program year: ') ||
        value.option.startsWith('Next program year: ')
      ) {
        const split = value.option.split(': ');
        if (split.length < 2) return defaultPeriod;
        const currentOption = options.find(x => x.startsWith(split[0]));
        if (!currentOption) return defaultPeriod;
        const option = currentOption.split(': ')?.[1]?.split(' – ');
        if (option.length < 2) return defaultPeriod;
        return {
          card: 'customYear',
          startDate: toDate(option[0]),
          endDate: toDate(option[1]),
        };
      }
      return defaultPeriod;
  }
};

export const getPeriodText = (
  period: CardPeriodType | string | undefined,
): string => {
  switch (period) {
    case 'academicYear':
      return 'Academic Year';
    case 'calendarYear':
      return 'Calendar Year';
    case 'fiscalYear':
      return 'Fiscal Year';
    default:
    case 'customYear':
      return 'Custom Year';
  }
};

const getParsePeriod = (value: string): CardPeriodType => {
  switch (value) {
    case 'CalendarYear':
      return 'calendarYear';
    case 'AcademicYear':
      return 'academicYear';
    case 'FiscalYear':
      return 'fiscalYear';
    // case 'CustomYear':
    default:
      return 'customYear';
  }
};

export interface ReportPeriod {
  startDate: Date | undefined;
  endDate: Date | undefined;
  card: CardPeriodType | undefined;
}

export const parsePeriodValuesString = (
  value: string | undefined,
): ReportPeriod => {
  if (!value) return defaultPeriod;
  const split = value.split(': ');
  if (split.length < 2) return defaultPeriod;
  const option = split[1].split(' - ');
  if (split.length < 2) return defaultPeriod;
  return {
    card: getParsePeriod(split[0]),
    startDate: toDate(option[0]),
    endDate: toDate(option[1]),
  };
};

export const parseReportPeriod = (period: string | undefined): ReportPeriod => {
  return period
    ? typeof period === 'string'
      ? parsePeriodValuesString(period)
      : period
    : {
        card: undefined,
        startDate: undefined,
        endDate: undefined,
      };
};

export const parsePeriodValues = (
  value: ParentPeriodValue | string | null,
  options: string[],
): {
  startDate: Date | undefined;
  endDate: Date | undefined;
  card: CardPeriodType | undefined;
} => {
  try {
    if (typeof value === 'string') return parsePeriodValuesString(value);
    return parsePeriodValuesObject(value, options);
  } catch (e) {
    return defaultPeriod;
  }
};

export const getPeriodStartEndDates = (
  periodType: CardPeriodType,
  year: number,
) => {
  if (!year) return undefined;
  switch (periodType) {
    case 'academicYear':
      return {
        option: periodType,
        startDate: new Date(year, 8, 1), // Sep 1st
        endDate: new Date(year + 1, 7, 31), // Aug 31st
      };
    case 'fiscalYear':
      return {
        option: periodType,
        startDate: new Date(year, 9, 1), // Oct 1st
        endDate: new Date(year + 1, 8, 30), // Sep 30th
      };
    default:
    case 'customYear':
    case 'calendarYear':
      return {
        option: periodType || 'customYear',
        startDate: new Date(year, 0, 1), // Jan 1st
        endDate: new Date(year, 11, 31), // Dec 31st
      };
  }
};

export const parseIndicatorSummary = (value: string | undefined) => {
  if (!value) return [];
  try {
    return JSON.parse(value.replace(/'/g, '"'));
  } catch (e) {
    return [];
  }
};

export const CHARITY_NAVIGATOR_MAX_START_DATE = 4 * 365; // 4 years

export const isCharityNavigatorDateValid = (
  value: PeriodString | ParentPeriodValue | undefined | null,
) => {
  if (!value) return false;

  const startDate = toDate(value.startDate);
  const maxAgeDate = new Date();
  maxAgeDate.setDate(maxAgeDate.getDate() - CHARITY_NAVIGATOR_MAX_START_DATE);
  if (!startDate || startDate < maxAgeDate) return false;

  const isPassed = isDatePassed(value.endDate);
  return isPassed;
};
